import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { keepPreviousData } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { Popover, PopoverAnchor, PopoverContent } from '../ui/popover'
import { $api } from '../../api/queryClient'
import { useDebounce } from '../../hooks/useDebounce'
import type { PropertyDefinition } from '../../hooks/useQueries'

export interface SuggestedTextInputProps {
    org: string
    propertyDefinition: PropertyDefinition
    /** Type of the asset being edited — required by an `asset_type` scoped property. */
    assetTypeUid?: string
    /** The edited asset's current values, keyed by property code, for `depends_on`. */
    context?: Record<string, string>
    value: string
    onChange: (value: string) => void
    onKeyDown?: (e: React.KeyboardEvent) => void
    disabled?: boolean
    hasError?: boolean
    placeholder?: string
    /** Defaults to `property-edit-text` so the plain-input e2e selectors keep working. */
    'data-testid'?: string
    autoFocus?: boolean
    /**
     * Bypasses the lookup and lists these values instead. Only for rendering
     * the component with fixed data (the `/$org/patterns` reference page);
     * production callers never set it.
     */
    staticSuggestions?: Suggestion[]
    /** Opens the list without waiting for focus. Static rendering only. */
    defaultOpen?: boolean
}

export interface Suggestion {
    value: string
    count: number
}

/**
 * Free-text combobox for a `text` property whose definition declares
 * `properties.suggestions` (spec 2026-09-02-03).
 *
 * It renders exactly like the plain text input it replaces — same classes, same
 * regex error message underneath — so nothing moves when a property gains
 * suggestions. Typing still stores whatever the user types; the values already
 * recorded on other assets are simply one keystroke away and rank first.
 *
 * The list is a plain listbox anchored under the input rather than a cmdk
 * `Command`: the input must keep focus (it *is* the value being edited), so the
 * highlight is driven from this component's own key handler instead of cmdk's
 * internal search input.
 */
export function SuggestedTextInput({
    org,
    propertyDefinition: propDef,
    assetTypeUid,
    context,
    value,
    onChange,
    onKeyDown,
    disabled = false,
    hasError = false,
    placeholder,
    'data-testid': dataTestId = 'property-edit-text',
    staticSuggestions,
    defaultOpen = false,
    // A combobox that is not focused shows nothing, so it takes focus by
    // default — the inline editor swaps this in when the user clicks the
    // value, and expects to be able to type straight away.
    autoFocus = true,
}: SuggestedTextInputProps) {
    const { t } = useTranslation('properties')
    const [open, setOpen] = useState(defaultOpen)
    const [highlight, setHighlight] = useState(-1)
    // Until the user types, the list is the property's top values — not the
    // values matching whatever is already stored. Opening the editor on
    // "Atlantic" must let you pick another brand, not just confirm this one.
    const [typed, setTyped] = useState(false)
    const inputRef = useRef<HTMLInputElement>(null)
    const contentRef = useRef<HTMLDivElement>(null)

    const debouncedValue = useDebounce(value, 250)
    const query = typed ? debouncedValue : ''

    const suggestionsConfig = propDef.properties?.suggestions as
        | { scope?: string; depends_on?: string[] }
        | undefined
    const scope = suggestionsConfig?.scope || 'asset_type'

    // Only the codes the definition depends on travel in the query, so an
    // unrelated edit elsewhere on the asset does not invalidate the cache.
    const contextParam = useMemo(() => {
        const dependsOn = suggestionsConfig?.depends_on ?? []
        if (dependsOn.length === 0 || !context) return undefined
        const picked: Record<string, string> = {}
        for (const code of dependsOn) {
            const v = context[code]
            if (v && v.trim()) picked[code] = v
        }
        return Object.keys(picked).length > 0 ? JSON.stringify(picked) : undefined
    }, [suggestionsConfig?.depends_on, context])

    // `asset_type` scope cannot be answered without a type; asking anyway would
    // only produce a 400 on every keystroke. The query is not gated on focus:
    // this component only exists while a value is being edited, and gating it
    // made the list disappear whenever the browser moved focus for a moment.
    const enabled = Boolean(
        !staticSuggestions && propDef.uid && suggestionsConfig
        && (scope !== 'asset_type' || assetTypeUid),
    )

    const { data } = $api.useQuery(
        'get',
        '/api/v1/organizations/{orgCode}/properties/{propertyUid}/suggestions',
        {
            params: {
                path: { orgCode: org, propertyUid: propDef.uid as string },
                query: {
                    ...(assetTypeUid ? { asset_type_uid: assetTypeUid } : {}),
                    ...(contextParam ? { context: contextParam } : {}),
                    ...(query ? { q: query } : {}),
                    limit: 10,
                },
            },
        },
        {
            enabled,
            staleTime: 30_000,
            // The query key carries the debounced text, so every few keystrokes
            // start a *new* query whose data is undefined until it answers.
            // Dropping to an empty list for that tick unmounted the popover mid
            // edit, which both flickered and broke the two-step Escape below
            // (with nothing on screen, the first Escape fell through to the
            // parent and cancelled the edit). Keep showing the previous answer
            // until the new one lands.
            placeholderData: keepPreviousData,
            // Non-blocking by design: a failed lookup must leave a plain,
            // working text input behind, never a notification.
            retry: false,
        },
    )

    const suggestions: Suggestion[] = useMemo(
        () => staticSuggestions ?? ((data as { data?: Suggestion[] } | undefined)?.data ?? []),
        [data, staticSuggestions],
    )

    // Silent while loading or empty — a property can declare suggestions long
    // before any asset carries a value, and a popover flickering in and out
    // would be worse than none.
    const shouldShow = open && suggestions.length > 0

    // Keyed on the values themselves, not the array identity: a refetch that
    // returns the same list must not reset the user's highlight (and must not
    // set state on every render).
    const suggestionKey = suggestions.map(s => s.value).join('\u0000')
    useEffect(() => {
        setHighlight(-1)
    }, [suggestionKey])

    const applySuggestion = useCallback((suggestion: Suggestion) => {
        onChange(suggestion.value)
        setOpen(false)
        setHighlight(-1)
        inputRef.current?.focus()
    }, [onChange])

    const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
        if (shouldShow) {
            if (e.key === 'ArrowDown') {
                e.preventDefault()
                setHighlight(h => (h + 1) % suggestions.length)
                return
            }
            if (e.key === 'ArrowUp') {
                e.preventDefault()
                setHighlight(h => (h <= 0 ? suggestions.length - 1 : h - 1))
                return
            }
            // Enter on a highlighted row fills the input without submitting;
            // a second Enter (nothing highlighted any more) saves.
            if (e.key === 'Enter' && highlight >= 0 && highlight < suggestions.length) {
                e.preventDefault()
                e.stopPropagation()
                applySuggestion(suggestions[highlight])
                return
            }
            // Escape closes the popover first; a second one reaches the parent
            // and cancels the edit.
            if (e.key === 'Escape') {
                e.preventDefault()
                e.stopPropagation()
                setOpen(false)
                setHighlight(-1)
                return
            }
        }
        onKeyDown?.(e)
    }, [shouldShow, suggestions, highlight, applySuggestion, onKeyDown])

    const hasPatternError = Boolean(propDef.properties?.regex_pattern && value && (() => {
        try {
            const regex = new RegExp(propDef.properties!.regex_pattern as string)
            return !regex.test(value)
        } catch {
            return false
        }
    })())

    return (
        <div className="flex-1">
            <Popover
                open={shouldShow}
                // Radix reports a close whenever `open` goes false — including
                // when it goes false only because the list momentarily has
                // nothing to show (the query is refetching under a new
                // context). Writing that back into `open` would latch the
                // popover shut for the rest of the edit, so only a dismissal
                // that happened while there *was* something on screen counts.
                onOpenChange={(next) => { if (!next && suggestions.length > 0) setOpen(false) }}
            >
                <PopoverAnchor asChild>
                    <input
                        ref={inputRef}
                        type="text"
                        value={value}
                        onChange={(e) => { onChange(e.target.value); setTyped(true); setOpen(true) }}
                        onKeyDown={handleKeyDown}
                        onFocus={() => setOpen(true)}
                        // Closing on a bare blur is wrong here: mounting the
                        // popover moves focus for a tick, and closing on that
                        // latched the list shut for the rest of the edit. Close
                        // only once focus has settled somewhere that is neither
                        // the input nor the list (tabbing to the save button);
                        // an outside click is Radix's own dismissal.
                        onBlur={() => {
                            window.setTimeout(() => {
                                const active = document.activeElement
                                if (inputRef.current?.contains(active) || contentRef.current?.contains(active)) return
                                setOpen(false)
                            }, 150)
                        }}
                        disabled={disabled}
                        autoFocus={autoFocus}
                        role="combobox"
                        aria-expanded={shouldShow}
                        aria-autocomplete="list"
                        aria-controls="property-suggestions"
                        className={`flex h-9 w-full rounded-md border ${hasPatternError || hasError ? 'border-destructive' : 'border-input'} bg-background px-3 py-1 text-sm shadow-sm transition-colors placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring`}
                        placeholder={placeholder ?? t('value_input.enter_text')}
                        data-testid={dataTestId}
                    />
                </PopoverAnchor>
                <PopoverContent
                    ref={contentRef}
                    align="start"
                    sideOffset={2}
                    className="p-0 w-(--radix-popover-trigger-width) min-w-56"
                    // Focus must stay in the input: the popover is a hint, not a
                    // separate widget to tab into.
                    onOpenAutoFocus={(e) => e.preventDefault()}
                    onCloseAutoFocus={(e) => e.preventDefault()}
                    // The input is the popover's *anchor*, not its trigger, so
                    // Radix counts focus and clicks landing on it as "outside"
                    // and dismisses — which closed the list the instant it
                    // opened. Focus never dismisses here (the input's own blur
                    // handler decides that); a pointer interaction dismisses
                    // only when it happened somewhere other than the input.
                    // Escape is handled by the input's own key handler, which
                    // closes the list and stops the event before the parent's
                    // cancel sees it. Radix's document-level listener would
                    // otherwise close the popover first, and the parent would
                    // then read a list that is already gone and cancel the edit
                    // on that very first Escape.
                    onEscapeKeyDown={(e) => e.preventDefault()}
                    onFocusOutside={(e) => e.preventDefault()}
                    onInteractOutside={(e) => {
                        if (inputRef.current?.contains(e.target as Node)) e.preventDefault()
                    }}
                >
                    <div
                        id="property-suggestions"
                        role="listbox"
                        data-testid="property-suggestions"
                        className="max-h-60 overflow-y-auto py-1"
                    >
                        {suggestions.map((suggestion, index) => (
                            <button
                                key={suggestion.value}
                                type="button"
                                role="option"
                                aria-selected={index === highlight}
                                data-testid={`property-suggestion-${index}`}
                                onMouseDown={(e) => e.preventDefault()}
                                onMouseEnter={() => setHighlight(index)}
                                onClick={() => applySuggestion(suggestion)}
                                className={`flex w-full items-center gap-2 px-3 py-1.5 text-left text-sm ${index === highlight ? 'bg-accent text-accent-foreground' : ''}`}
                            >
                                <span className="flex-1 truncate">
                                    {renderMatch(suggestion.value, query)}
                                </span>
                                <span className="shrink-0 text-xs text-muted-foreground">
                                    {t('suggestions.count', { count: suggestion.count })}
                                </span>
                            </button>
                        ))}
                    </div>
                    {value ? (
                        <div
                            className="border-t px-3 py-1.5 text-xs text-muted-foreground"
                            data-testid="property-suggestions-free-text-hint"
                        >
                            {t('suggestions.free_text_hint', { value })}
                        </div>
                    ) : null}
                </PopoverContent>
            </Popover>
            {hasPatternError ? (
                <p className="text-xs text-destructive mt-1">
                    {t('value_input.pattern_constraint', { pattern: (propDef.properties?.regex_pattern as string) || '' })}
                </p>
            ) : null}
        </div>
    )
}

/** Bolds the part of `value` that the typed text matches as a prefix. */
function renderMatch(value: string, typed: string) {
    if (!typed) return value
    const lowerValue = value.toLowerCase()
    const lowerTyped = typed.toLowerCase()
    if (!lowerValue.startsWith(lowerTyped)) return value
    return (
        <>
            <span className="font-semibold">{value.slice(0, typed.length)}</span>
            {value.slice(typed.length)}
        </>
    )
}
